import { FAR, NEAR, OUTLINE } from "../shared/defs";
import { css, hex } from "../shared/color";
import { spriteRot, type Sprite } from "./art";
import { drawRot, type RotFrame } from "./rotsprite";
import { SCALE, Rig, type Pen, type RigInput } from "./rig";
import { skinsFor } from "./skins";

export interface Pose {
  x: number;
  y: number;
  facing: number;
  aim: number;
  vx: number;
  vy?: number;
  grounded: boolean;
  phase: number;
  jet: number;
  flip: number;
  tumble: number;
  skin: string;
  dt?: number;
  t?: number;
  kick?: number;
  kickRot?: number;
  who?: string;
  holdDist?: number;
  holdDrop?: number;
  twoHand?: boolean;
  grasp?: boolean;
  throwT?: number;
  cast?: number;
  recover?: number;
  /** When true, the rig was already integrated this tick and draw should not step it again. */
  ready?: boolean;
  ground?: (x: number, y: number) => number;
  /** Title thumbs skip the additive glow, which otherwise paints an opaque disc. */
  skipGlow?: boolean;
  /** Sit on a ledge with the hips at y and the feet dangling below it. */
  sit?: boolean;
}

export interface Hold {
  sprite: Sprite;
  rotFrame: (angle: number, flip: boolean) => RotFrame;
  anchor: (x: number, y: number, angle: number, flip: boolean) => [number, number];
  quantize: (angle: number) => number;
  grip: [number, number];
  muzzle: [number, number];
  fore: [number, number] | null;
  twoHand: boolean;
  grasp: boolean;
}

interface Spark {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  max: number;
  color: number;
}

class Actor {
  rig = new Rig();
  sparks: Spark[] = [];
  private stamp = Number.NaN;
  canvas = document.createElement("canvas");
  glow = document.createElement("canvas");
  pen: Pen;

  constructor() {
    this.canvas.width = 128;
    this.canvas.height = 128;
    this.glow.width = 128;
    this.glow.height = 128;
    const rig = this.rig;
    this.pen = {
      rig,
      data: {},
      near: 0,
      far: 0,
      dt: 1 / 60,
      t: 0,
      rand: Math.random,
      cap: (a, b, radius, color) => rig.cap(a, b, radius, color),
      disc: (x, y, radius, color) => rig.disc(x, y, radius, color),
      px: (x, y, color, mask = 2) => {
        const lx = Math.floor(x) - rig.local.ox;
        const ly = Math.floor(y) - rig.local.oy;
        if (lx < 0 || ly < 0 || lx >= rig.local.w || ly >= rig.local.h) return;
        rig.local.data[ly * rig.local.w + lx] = color >>> 0;
        rig.mask[ly * rig.local.w + lx] = mask;
      },
      glow: (x, y, radius, color, amount) => rig.glowBuf.glow(x, y, radius, color, amount),
      emit: (kind, x, y, vx, vy, life, color) => {
        void kind;
        if (this.sparks.length > 40) this.sparks.shift();
        this.sparks.push({ x, y, vx, vy, life, max: life, color });
      },
    };
  }

  update(pose: Pose, hold: Hold | null) {
    const dt = pose.dt ?? 1 / 60;
    const stamp = pose.t ?? this.rig.now.t + dt;
    if (stamp === this.stamp) return;
    this.stamp = stamp;
    const look = skinsFor(pose.skin);
    const input: RigInput = {
      dt,
      t: stamp,
      x: pose.x,
      y: pose.y,
      vx: pose.vx,
      vy: pose.vy ?? 0,
      grounded: pose.grounded,
      facing: pose.facing || 1,
      aim: pose.aim,
      holdDist: pose.holdDist ?? (hold ? 16 : 16),
      holdDrop: pose.holdDrop ?? 0,
      twoHanded: pose.twoHand ?? !!hold?.twoHand,
      kick: pose.kick ?? 0,
      kickRot: pose.kickRot ?? 0,
      foregrip: (x, y, angle) => {
        if (!hold?.fore) return { x, y };
        const flip = Math.cos(angle) < 0;
        const [dx, dy] = hold.anchor(hold.fore[0], hold.fore[1], hold.quantize(angle), flip);
        return { x: x + dx * SCALE, y: y + dy * SCALE };
      },
      ground: pose.ground ?? (() => pose.y),
      flip: pose.flip,
      tumble: pose.tumble,
      throwT: pose.throwT ?? -1,
      cast: pose.cast ?? -1,
      recover: pose.recover ?? -1,
      jet: pose.jet,
      sit: !!pose.sit,
    };
    this.rig.update(input);
    this.pen.dt = dt;
    this.pen.t = input.t;
    this.pen.near = look.near;
    this.pen.far = look.far;
    for (const skin of look.wear) {
      if (!skin.step) continue;
      this.pen.data = this.rig.dataOf(skin.id);
      skin.step(this.pen);
    }
    for (const spark of this.sparks) {
      spark.life -= dt;
      spark.x += spark.vx * dt;
      spark.y += spark.vy * dt;
      spark.vy += 40 * dt;
    }
    this.sparks = this.sparks.filter((spark) => spark.life > 0);
  }

  render(ctx: CanvasRenderingContext2D, pose: Pose, hold: Hold | null, jet: Sprite | null, jetPivot: number[] | null) {
    const look = skinsFor(pose.skin);
    const rig = this.rig;
    const flip = Math.cos(rig.gunAngle) < 0;
    const gun = hold ? hold.rotFrame(rig.gunAngle, flip) : null;
    let pack: { sprite: RotFrame["sprite"]; ox: number; oy: number; fade?: number } | null = null;
    if (pose.jet > 0.02 && jet && jetPivot) {
      const rot = spriteRot(jet, jetPivot[0], jetPivot[1], 64);
      const torsoX = rig.chest.x - rig.pelvis.x;
      const torsoY = rig.chest.y - rig.pelvis.y;
      const len = Math.hypot(torsoX, torsoY) || 1;
      const ux = torsoX / len;
      const uy = torsoY / len;
      const facing = pose.facing || 1;
      const px = uy * facing;
      const py = -ux * facing;
      const x = rig.shoulder.x + px * 9.5 - ux * 4;
      const y = rig.shoulder.y + py * 9.5 - uy * 4;
      const angle = Math.atan2(ux, -uy);
      const frame = rot.get(angle, false);
      pack = { sprite: frame.sprite, ox: Math.round(x / SCALE - 0.5) - frame.ox, oy: Math.round(y / SCALE - 0.5) - frame.oy, fade: Math.min(1, pose.jet * 3) };
    }
    this.rig.paint({
      near: look.near,
      far: look.far,
      outline: look.outline,
      shadow: hex("#000000"),
      twoHanded: pose.twoHand ?? !!hold?.twoHand,
      grasp: pose.grasp ?? !!hold?.grasp,
      gun: gun ? { sprite: gun.sprite, ox: gun.ox, oy: gun.oy } : null,
      pack,
      wear: look.wear,
      hideTorso: look.hideTorso,
      hideArms: look.hideArms,
      hideLegs: look.hideLegs,
      hideHead: look.wear.some((skin) => !!skin.fullHead),
      pen: this.pen,
    });
    const local = this.rig.local;
    const g = this.canvas.getContext("2d")!;
    g.putImageData(new ImageData(local.bytes as Uint8ClampedArray<ArrayBuffer>, local.w, local.h), 0, 0);
    const glow = this.rig.glowBuf;
    const gg = this.glow.getContext("2d")!;
    gg.putImageData(new ImageData(glow.bytes as Uint8ClampedArray<ArrayBuffer>, glow.w, glow.h), 0, 0);
    const dx = local.ox * SCALE;
    const dy = local.oy * SCALE;
    ctx.save();
    ctx.imageSmoothingEnabled = false;
    ctx.globalAlpha = 0.28;
    ctx.drawImage(this.canvas, dx + SCALE, dy + SCALE, local.w * SCALE, local.h * SCALE);
    ctx.globalAlpha = 1;
    ctx.drawImage(this.canvas, dx, dy, local.w * SCALE, local.h * SCALE);
    if (!pose.skipGlow) {
      ctx.globalCompositeOperation = "lighter";
      ctx.drawImage(this.glow, dx, dy, glow.w * SCALE, glow.h * SCALE);
      ctx.globalCompositeOperation = "source-over";
    }
    if (!pose.skipGlow) {
      for (const spark of this.sparks) {
        ctx.globalAlpha = Math.max(0, spark.life / spark.max);
        ctx.fillStyle = css(spark.color);
        ctx.fillRect(spark.x, spark.y, 2, 2);
      }
    }
    ctx.restore();
  }
}

const actors = new Map<string, Actor>();

function actorFor(who: string) {
  let actor = actors.get(who);
  if (!actor) actors.set(who, actor = new Actor());
  return actor;
}

/** Integrate the rig once per simulation tick, before shots read the muzzle. */
export function syncStick(pose: Pose, hold: Hold | null) {
  actorFor(pose.who ?? "local").update(pose, hold);
}

export function drawStick(ctx: CanvasRenderingContext2D, pose: Pose, hold: Hold | null, jet: Sprite | null, jetPivot: number[] | null) {
  const actor = actorFor(pose.who ?? "local");
  if (!pose.ready) actor.update(pose, hold);
  actor.render(ctx, pose, hold, jet, jetPivot);
  void NEAR;
  void FAR;
  void OUTLINE;
}

/** Grasp hand. Punchline leaves from here. Syncs the rig when this pose has not been posed yet. */
export function nearHand(pose: Pose, hold: Hold | null): { x: number; y: number } {
  const actor = actorFor(pose.who ?? "local");
  if (!pose.ready) actor.update(pose, hold);
  return { x: actor.rig.handN.x, y: actor.rig.handN.y };
}

/** Grasp hand after drawStick. Does not integrate the rig a second time. */
export function peekNearHand(who = "local"): { x: number; y: number } {
  const actor = actorFor(who);
  return { x: actor.rig.handN.x, y: actor.rig.handN.y };
}

/** The hand that lets go of a grenade: the near hand on a two-handed gun, otherwise the free hand. */
export function handOf(pose: Pose, hold: Hold | null, twoHanded: boolean): { x: number; y: number } {
  const actor = actorFor(pose.who ?? "local");
  if (!pose.ready) actor.update(pose, hold);
  const hand = twoHanded ? actor.rig.handN : actor.rig.handF;
  return { x: hand.x, y: hand.y };
}

/** Jetpack mount: shoulder, then across the back. The drone launches from here. */
export function packPoint(pose: Pose): { x: number; y: number } {
  const actor = actorFor(pose.who ?? "local");
  if (!pose.ready) actor.update(pose, null);
  const rig = actor.rig;
  const torsoX = rig.chest.x - rig.pelvis.x;
  const torsoY = rig.chest.y - rig.pelvis.y;
  const len = Math.hypot(torsoX, torsoY) || 1;
  const ux = torsoX / len;
  const uy = torsoY / len;
  const facing = pose.facing || 1;
  const px = uy * facing;
  const py = -ux * facing;
  return { x: rig.shoulder.x + px * 9.5 - ux * 4, y: rig.shoulder.y + py * 9.5 - uy * 4 };
}

export function muzzleOf(pose: Pose, hold: Hold | null): { x: number; y: number } {
  const actor = actorFor(pose.who ?? "local");
  if (!pose.ready) actor.update(pose, hold);
  const rig = actor.rig;
  if (!hold) return { x: rig.grip.x, y: rig.grip.y };
  const flip = Math.cos(rig.gunAngle) < 0;
  const [dx, dy] = hold.anchor(hold.muzzle[0], hold.muzzle[1], hold.quantize(rig.gunAngle), flip);
  return { x: rig.grip.x + dx * SCALE, y: rig.grip.y + dy * SCALE };
}

export function makeHold(sprite: Sprite, grip: number[], muzzle: number[], fore: number[] | null, twoHand: boolean, grasp: boolean, steps = 128): Hold {
  const rot = spriteRot(sprite, grip[0], grip[1], steps);
  return {
    sprite,
    rotFrame: (angle, flip) => rot.get(angle, flip),
    anchor: (x, y, angle, flip) => rot.anchor(x, y, angle, flip),
    quantize: (angle) => rot.quantize(angle),
    grip: [grip[0] - sprite.trimX, grip[1] - sprite.trimY],
    muzzle: [muzzle[0] - sprite.trimX, muzzle[1] - sprite.trimY],
    fore: fore ? [fore[0] - sprite.trimX, fore[1] - sprite.trimY] : null,
    twoHand,
    grasp,
  };
}
